import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('protego_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('protego_token'));

  const loadUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    // If we already have a cached user for this token, avoid blocking the app on /auth/user.
    if (user) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.me();
      const userData = res.data?.user || res.data;
      setUser(userData);
    } catch {
      localStorage.removeItem('protego_token');
      localStorage.removeItem('protego_user');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const data = res.data;
    const accessToken = data.tokens?.access || data.access_token;
    const userData = data.user;
    if (!accessToken || !userData) {
      throw new Error('Invalid login response from server');
    }
    // Always force explicit account/profile selection on login.
    localStorage.removeItem('protego_account_id');
    localStorage.removeItem('protego_profile_id');
    localStorage.setItem('protego_token', accessToken);
    localStorage.setItem('protego_user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
    return userData;
  };

  const signup = async (name, email, password) => {
    const nameParts = name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const res = await authAPI.signup({
      name,
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      password2: password,
    });
    const data = res.data;
    const accessToken = data.tokens?.access || data.access_token;
    const userData = data.user;
    if (!accessToken || !userData) {
      throw new Error('Invalid signup response from server');
    }
    // Always force explicit account/profile selection on signup login.
    localStorage.removeItem('protego_account_id');
    localStorage.removeItem('protego_profile_id');
    localStorage.setItem('protego_token', accessToken);
    localStorage.setItem('protego_user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      // proceed even if server logout fails
    }
    localStorage.removeItem('protego_token');
    localStorage.removeItem('protego_user');
    localStorage.removeItem('protego_account_id');
    localStorage.removeItem('protego_profile_id');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, isAdmin: user?.role === 'admin' || user?.role === 'superadmin' }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
